import { useSupportStore } from '@/entities/support';
import { SUPPORT_REQUEST_STATUS_ENUM } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useMemo } from 'react';
import type { SupportRequestInfoProps } from './supportRequestInfo.types';

/**
 * Supplies the support request info panel with its facilities, notification emails and edit modal.
 * Reads supportFacilities from the support store, splits replyTo into a cleaned email list, marks
 * the request open when its status is New, and toggles the edit-email modal flag.
 *
 * @param props - Props carrying the request's detailData.
 * @returns An object carrying supportFacilities, state, listEmail, isOpen and handleToggleModal.
 */
export const useSupportRequestInfo = ({
  detailData,
}: Pick<SupportRequestInfoProps, 'detailData'>) => {
  const { replyTo } = detailData || {};

  /**
   * Support facilities available to the current user. Read from the support store through a selector.
   *
   * @returns The support facilities held in the store.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  const [state, setState] = useSetState({
    isOpenEditModal: false,
    isSavingEditModal: false,
  });

  /**
   * Notification email addresses of the support request. Splits detailData.replyTo on commas, trims each entry and drops the empty ones, recomputed when replyTo changes.
   *
   * @returns The cleaned list of reply-to addresses.
   */
  const listEmail = useMemo(() => {
    /**
     * Trimmed entries of the support request reply-to string. Splits replyTo on commas and trims every resulting item.
     *
     * @returns Raw reply-to entries after trimming.
     */
    const rawData = _.map(_.split(replyTo, ','), (item) => _.trim(item));
    /**
     * Non-empty entries of the split reply-to list. Filters the trimmed items, keeping only those that are not empty.
     *
     * @returns Reply-to addresses without blank entries.
     */
    const filterData = _.filter(rawData, (item) => !_.isEmpty(item));
    return filterData;
  }, [replyTo]);

  const isOpen = detailData?.status === SUPPORT_REQUEST_STATUS_ENUM.New;

  /**
   * Shows or hides the modal for editing the support request notification emails. Flips isOpenEditModal through the previous-state form of setState.
   *
   * @returns Nothing.
   */
  const handleToggleModal = () => {
    setState(({ isOpenEditModal }) => ({ isOpenEditModal: !isOpenEditModal }));
  };

  return {
    supportFacilities,
    state,
    listEmail,
    isOpen,
    handleToggleModal,
  };
};
